import { classes } from "lib/actions/util";
import { HTMLAttributes, useEffect } from "react";
import { useSpatialViewer } from "lib/contexts/SpatialContexts/SpatialViewerContext";
import { useMeasureRef } from "lib/hooks/useMeasureRef";

/**
 * The Cesium viewer is permanently present in the app, it never gets destroyed.
 * The Anchor controls whether the viewer is visible and where to place it. On
 * layouts with no viewer, for instance the layout used for project settings,
 * the anchor gets unmounted and therefore the viewer is hidden but not unmounted.
 * This is so that the state of the Cesium viewer is not lost when navigating to
 * different routes, which don't show the viewer, and back.
 */
const CesiumViewerAnchor = ({
  isHidden = false,
  ...p
}: HTMLAttributes<HTMLDivElement> & {
  /**
   * Hides the viewer while keeping it at the anchor's full size. Unmounting
   * shrinks it to nothing, which stops the camera flying and tiles streaming.
   */
  isHidden?: boolean;
}) => {
  const { setPosition, setIsHidden } = useSpatialViewer();

  useEffect(() => {
    return () => {
      setPosition({ x: 0, y: 0, w: 0, h: 0 });
    };
  }, []);

  useEffect(() => {
    setIsHidden(isHidden);
    return () => setIsHidden(false);
  }, [isHidden]);

  const setRef = useMeasureRef((bb) => {
    setPosition({
      x: bb.left,
      y: bb.top,
      w: bb.width,
      h: bb.height,
    });
  });

  return (
    <div ref={setRef} {...p} className={classes("invisible", p.className)} />
  );
};

export default CesiumViewerAnchor;
